import { useState } from 'react';
import { Shield, FileText, Users, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { useBackgrounds } from '../context/BackgroundContext';
import axios from 'axios';
import { Toaster, toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const doctrines = [
    {
        title: 'Code of the Pack',
        items: [
            'Honor above all - we fight with integrity',
            'Wolves hunt together - no pilot left behind',
            'Respect the chain of command',
            'Continuous training and improvement',
            'Protect civilian assets at all costs'
        ]
    },
    {
        title: 'Combat Doctrine',
        items: [
            'Strike fast, strike hard, vanish',
            'Maintain formation discipline',
            'Prioritize high-value targets',
            'Adapt to changing battlefield conditions',
            'Secure extraction routes before engagement'
        ]
    },
    {
        title: 'Communication Protocol',
        items: [
            'Clear and concise comms during ops',
            'Use designated callsigns only',
            'Report contacts immediately',
            'Acknowledge all orders',
            'Maintain radio discipline'
        ]
    }
];

const roles = [
    'Combat Pilot',
    'Bomber Pilot',
    'Transport Pilot',
    'Marine Operator',
    'Logistics Specialist',
    'Engineer',
    'Medical Officer',
    'Intelligence Analyst'
];

const timezones = [
    'UTC-8 (PST)',
    'UTC-7 (MST)',
    'UTC-6 (CST)',
    'UTC-5 (EST)',
    'UTC+0 (GMT)',
    'UTC+1 (CET)',
    'UTC+8 (SGT)',
    'UTC+9 (JST)',
    'UTC+10 (AEST)'
];

export const WarRoom = () => {
    const { getBackground } = useBackgrounds();
    const [activeSection, setActiveSection] = useState('enlist');
    const [formData, setFormData] = useState({
        callsign: '',
        email: '',
        discord_id: '',
        timezone: '',
        play_hours: '',
        experience: '',
        preferred_role: '',
        why_join: '',
        referred_by: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    
    const warRoomBg = getBackground('war_room');

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        try {
            await axios.post(`${API}/applications`, formData);
            setSubmitted(true);
            toast.success('Application submitted successfully!');
        } catch (error) {
            console.error('Failed to submit application:', error);
            toast.error('Failed to submit application. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#050505] pt-16" data-testid="war-room-page">
            <Toaster position="top-right" theme="dark" />
            
            {/* Hero */}
            <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
                <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url(${warRoomBg})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#050505]" />
                <div className="relative z-10 text-center px-4">
                    <span className="font-mono text-xs text-red-500 tracking-[0.3em]">TACTICAL OPERATIONS CENTER</span>
                    <h1 className="font-heading font-black text-4xl sm:text-5xl text-white uppercase mt-2">
                        War Room
                    </h1>
                    <p className="font-mono text-sm text-gray-400 mt-4">
                        Doctrine • Recruitment • Enlistment Portal
                    </p>
                </div>
            </section>

            {/* Navigation Tabs */}
            <section className="px-4 border-b border-white/10">
                <div className="max-w-6xl mx-auto">
                    <div className="flex gap-4">
                        <button
                            onClick={() => setActiveSection('doctrine')}
                            className={`py-4 px-6 font-mono text-sm uppercase tracking-wider transition-colors ${
                                activeSection === 'doctrine' 
                                    ? 'text-red-500 border-b-2 border-red-500' 
                                    : 'text-gray-500 hover:text-gray-300'
                            }`}
                            data-testid="tab-doctrine"
                        >
                            <FileText className="w-4 h-4 inline mr-2" />
                            Doctrine
                        </button>
                        <button
                            onClick={() => setActiveSection('recruiting')}
                            className={`py-4 px-6 font-mono text-sm uppercase tracking-wider transition-colors ${
                                activeSection === 'recruiting' 
                                    ? 'text-red-500 border-b-2 border-red-500' 
                                    : 'text-gray-500 hover:text-gray-300'
                            }`}
                            data-testid="tab-recruiting"
                        >
                            <Users className="w-4 h-4 inline mr-2" />
                            Recruiting
                        </button>
                        <button
                            onClick={() => setActiveSection('enlist')}
                            className={`py-4 px-6 font-mono text-sm uppercase tracking-wider transition-colors ${
                                activeSection === 'enlist' 
                                    ? 'text-red-500 border-b-2 border-red-500' 
                                    : 'text-gray-500 hover:text-gray-300'
                            }`}
                            data-testid="tab-enlist"
                        >
                            <Shield className="w-4 h-4 inline mr-2" />
                            Enlist
                        </button>
                    </div>
                </div>
            </section>

            {/* Content */}
            <section className="px-4 py-12">
                <div className="max-w-6xl mx-auto">
                    
                    {/* Doctrine Section */}
                    {activeSection === 'doctrine' && (
                        <div className="grid md:grid-cols-3 gap-6" data-testid="doctrine-content">
                            {doctrines.map((doctrine, index) => (
                                <div key={index} className="tactical-panel p-6">
                                    <h3 className="font-heading font-bold text-lg text-white uppercase mb-4 flex items-center gap-2">
                                        <Shield className="w-4 h-4 text-red-500" />
                                        {doctrine.title}
                                    </h3>
                                    <ul className="space-y-3">
                                        {doctrine.items.map((item, i) => (
                                            <li key={i} className="flex items-start gap-3 text-sm text-gray-400">
                                                <span className="text-red-500 mt-1">»</span>
                                                {item}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Recruiting Section */}
                    {activeSection === 'recruiting' && (
                        <div data-testid="recruiting-content">
                            <div className="tactical-panel p-8 mb-8">
                                <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">
                                    Join the 126th Carrier Strike Group
                                </h2>
                                <p className="font-body text-gray-400 mb-6 max-w-3xl">
                                    The Voidwolves are an elite carrier-based strike group operating across the Stanton system 
                                    and beyond. We are actively recruiting dedicated pilots, marine operators, and support 
                                    personnel to join our ranks.
                                </p>
                                <div className="grid md:grid-cols-2 gap-6">
                                    <div>
                                        <h3 className="font-mono text-sm text-red-500 uppercase tracking-wider mb-3">What We Offer</h3>
                                        <ul className="space-y-2 text-gray-400 text-sm">
                                            <li>• Organized military-style operations</li>
                                            <li>• Carrier-based strike missions</li>
                                            <li>• Active community and Discord</li>
                                            <li>• Training programs for all skill levels</li>
                                            <li>• Regular events and fleet operations</li>
                                        </ul>
                                    </div>
                                    <div>
                                        <h3 className="font-mono text-sm text-red-500 uppercase tracking-wider mb-3">Requirements</h3>
                                        <ul className="space-y-2 text-gray-400 text-sm">
                                            <li>• 18+ years of age</li>
                                            <li>• Working microphone</li>
                                            <li>• Discord account</li>
                                            <li>• Respectful attitude</li>
                                            <li>• Willingness to learn and improve</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <div className="grid md:grid-cols-4 gap-4">
                                {roles.map((role) => (
                                    <div key={role} className="bg-black/50 border border-white/10 p-4 text-center hover:border-red-500/30 transition-colors">
                                        <span className="font-mono text-sm text-gray-300">{role}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Enlistment Form Section */}
                    {activeSection === 'enlist' && (
                        <div className="max-w-2xl mx-auto" data-testid="enlist-content">
                            {submitted ? (
                                <div className="tactical-panel p-12 text-center" data-testid="submission-success">
                                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
                                    <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">
                                        Application Received
                                    </h2>
                                    <p className="font-body text-gray-400 mb-6">
                                        Your enlistment application has been submitted successfully. 
                                        Our recruitment officers will review your application and contact you via Discord.
                                    </p>
                                    <p className="font-mono text-xs text-gray-500">
                                        THE VOID PROVIDES. THE PACK ENDURES.
                                    </p>
                                </div>
                            ) : (
                                <div className="tactical-panel p-8">
                                    <div className="text-center mb-8">
                                        <h2 className="font-heading font-bold text-2xl text-white uppercase">
                                            Enlistment Portal
                                        </h2>
                                        <p className="font-mono text-xs text-gray-500 mt-2 tracking-wider">
                                            SECURE TRANSMISSION // RECRUITMENT DIVISION
                                        </p>
                                    </div>

                                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="enlistment-form">
                                        <div className="grid md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="form-label">Callsign / Handle *</label>
                                                <input
                                                    type="text"
                                                    name="callsign"
                                                    value={formData.callsign}
                                                    onChange={handleInputChange}
                                                    required
                                                    className="form-input"
                                                    placeholder="Your in-game callsign"
                                                    data-testid="input-callsign"
                                                />
                                            </div>
                                            <div>
                                                <label className="form-label">Email *</label>
                                                <input
                                                    type="email"
                                                    name="email"
                                                    value={formData.email}
                                                    onChange={handleInputChange}
                                                    required
                                                    className="form-input"
                                                    placeholder="your@email.com"
                                                    data-testid="input-email"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="form-label">Discord ID *</label>
                                                <input
                                                    type="text"
                                                    name="discord_id"
                                                    value={formData.discord_id}
                                                    onChange={handleInputChange}
                                                    required
                                                    className="form-input"
                                                    placeholder="username#0000"
                                                    data-testid="input-discord"
                                                />
                                            </div>
                                            <div>
                                                <label className="form-label">Timezone *</label>
                                                <select
                                                    name="timezone"
                                                    value={formData.timezone}
                                                    onChange={handleInputChange}
                                                    required
                                                    className="form-input"
                                                    data-testid="input-timezone"
                                                >
                                                    <option value="">Select timezone</option>
                                                    {timezones.map((tz) => (
                                                        <option key={tz} value={tz}>{tz}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <div className="grid md:grid-cols-2 gap-6">
                                            <div>
                                                <label className="form-label">Weekly Play Hours *</label>
                                                <input
                                                    type="text"
                                                    name="play_hours"
                                                    value={formData.play_hours}
                                                    onChange={handleInputChange}
                                                    required
                                                    className="form-input"
                                                    placeholder="e.g., 10-20 hours"
                                                    data-testid="input-hours"
                                                />
                                            </div>
                                            <div>
                                                <label className="form-label">Preferred Role *</label>
                                                <select
                                                    name="preferred_role"
                                                    value={formData.preferred_role}
                                                    onChange={handleInputChange}
                                                    required
                                                    className="form-input"
                                                    data-testid="input-role"
                                                >
                                                    <option value="">Select role</option>
                                                    {roles.map((role) => (
                                                        <option key={role} value={role}>{role}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="form-label">Star Citizen Experience *</label>
                                            <textarea
                                                name="experience"
                                                value={formData.experience}
                                                onChange={handleInputChange}
                                                required
                                                rows={3}
                                                className="form-input"
                                                placeholder="Describe your Star Citizen experience, ships owned, etc."
                                                data-testid="input-experience"
                                            />
                                        </div>

                                        <div>
                                            <label className="form-label">Why do you want to join the 126th? *</label>
                                            <textarea
                                                name="why_join"
                                                value={formData.why_join}
                                                onChange={handleInputChange}
                                                required
                                                rows={3}
                                                className="form-input"
                                                placeholder="Tell us why you want to be a Voidwolf."
                                                data-testid="input-why-join"
                                            />
                                        </div>

                                        <div>
                                            <label className="form-label">Referred By (Optional)</label>
                                            <input
                                                type="text"
                                                name="referred_by"
                                                value={formData.referred_by}
                                                onChange={handleInputChange}
                                                className="form-input"
                                                placeholder="Member who referred you."
                                                data-testid="input-referred"
                                            />
                                        </div>

                                        <div className="pt-4">
                                            <button
                                                type="submit"
                                                disabled={submitting}
                                                className="btn-primary w-full py-4 flex items-center justify-center gap-2"
                                                data-testid="submit-application-button"
                                            >
                                                {submitting ? (
                                                    <>Processing...</>
                                                ) : (
                                                    <>
                                                        <Send className="w-4 h-4" />
                                                        Submit Enlistment
                                                    </>
                                                )}
                                            </button>
                                        </div>

                                        <p className="font-mono text-[10px] text-gray-600 text-center">
                                            By submitting, you agree to follow the Code of the Pack
                                        </p>
                                    </form>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
};
